// Libs
import classNames from 'classnames/bind';
import { useEffect, useState } from 'react';
import {
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  IconButton,
  Stack,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
// Components, Layouts, Pages
import { BaseInput, BaseLabel, Loading } from '~/components/common';
// Others
import { defaultValuesEventTypeForm, schema } from './helper';
import { IBodyUpdate } from '~/utils/interface/common';
import { EMPTY_STRING } from '~/utils/constants/common';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { compareDataUpdate } from '~/utils/helper/common';
import { IBodyCreateMasterData, IFormMasterData } from '~/utils/interface/masterData';
import { createMasterData, getDetailMasterData, updateMasterData } from '~/thunks/masterData/masterDataThunk';
import { TypeMasterDataListEnum } from '~/utils/enum/common';
// Styles, images, icons
import styles from './EventTypeForm.module.scss';
import { icons } from '~/assets';

type Props = {
  idEventType?: string;
  onClose: () => void;
  onRemoveFile?: (id: string) => void;
};

const cx = classNames.bind(styles);

const EventTypeFormModal = (props: Props) => {
  //#region Destructuring Props
  const { idEventType, onClose } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/eventType', 'common/base']);
  const dispatch = useAppDispatch();
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<IFormMasterData>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesEventTypeForm,
  });
  //#endregion Declare Hook

  //#region Selector
  const { isLoading } = useAppSelector((state) => state.coupon);
  //#endregion Selector

  //#region Declare State
  const [initialData, setInitialData] = useState<IFormMasterData>(defaultValuesEventTypeForm);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!idEventType) return;

    handleGetDetailEventType(idEventType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idEventType]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleCloseModal = () => {
    onClose && onClose();
    reset();
  };

  const handleFormEventType = (data: IFormMasterData) => {
    if (idEventType) {
      handleEditEventType(data);
    } else {
      handleAddEventType(data);
    }
  };

  const handleAddEventType = async (data: IFormMasterData) => {
    const dataForm: IBodyCreateMasterData = {
      name: data.name,
      type: TypeMasterDataListEnum.EVENT,
    };

    dispatch(createMasterData(dataForm))
      .unwrap()
      .then((_res) => {
        handleCloseModal();
      })
      .catch((_error) => {})
      .finally(() => {});
  };

  const handleEditEventType = async (data: IFormMasterData) => {
    const dataChange = compareDataUpdate(initialData, data) as IFormMasterData;

    const params: IBodyUpdate<IFormMasterData> = {
      id: idEventType || EMPTY_STRING,
      body: dataChange,
    };

    dispatch(updateMasterData(params))
      .unwrap()
      .then(() => {
        handleCloseModal();
      })
      .catch(() => {})
      .finally(() => {});
  };

  const handleGetDetailEventType = (idEventType: string) => {
    if (!idEventType) return;

    dispatch(getDetailMasterData(idEventType))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        const { id, ...remainingData } = res?.data;

        const body: IFormMasterData = {
          ...remainingData,
          name: remainingData?.name || EMPTY_STRING,
        };

        setInitialData(body);

        reset(body);
      })
      .catch((_error) => {})
      .finally(() => {});
  };
  //#endregion Handle Function

  return (
    <Dialog
      id='EventTypeFormModalComponent'
      aria-labelledby='EventTypeFormModal'
      open={true}
      onClose={handleCloseModal}
      fullWidth
      maxWidth={'md'}
    >
      <DialogTitle id='eventTypeFormModal' className={cx('header')}>
        <Stack direction={'column'} spacing={'10px'}>
          <Stack direction={'row'} spacing={10}>
            <BaseLabel
              label={t(idEventType ? 'form_add_event_type_title_edit' : 'form_add_event_type_title')}
              styleType='bigTitle'
            />
          </Stack>
        </Stack>

        <IconButton onClick={handleCloseModal} className={cx('buttonClose')}>
          <Avatar
            src={icons.commonIconCancel}
            className={cx('iconClose')}
            alt={t('common_alt_img', { ns: 'common/base' })}
          />
        </IconButton>
      </DialogTitle>

      <DialogContent className={cx('content')} dividers>
        <Grid2 columnSpacing={'24px'} rowSpacing={'24px'} container>
          <Grid2 size={6}>
            <Controller
              control={control}
              name={'name'}
              render={({ field: { onChange, value } }) => {
                return (
                  <BaseInput
                    height={36}
                    label={t('form_add_event_type_name_title')}
                    value={value}
                    onChange={onChange}
                    errorMessage={errors?.name?.message}
                    required
                  />
                );
              }}
            />
          </Grid2>
        </Grid2>
      </DialogContent>

      <DialogActions className={cx('groupButton')}>
        <Button variant='outlined' className={cx('buttonCancel')} onClick={handleCloseModal}>
          {t('common_action_cancel', { ns: 'common/base' })}
        </Button>

        <Button
          variant='contained'
          className={cx('buttonSave')}
          onClick={handleSubmit(handleFormEventType)}
          disabled={idEventType ? !isDirty : false}
        >
          {t('common_action_save', { ns: 'common/base' })}
        </Button>
      </DialogActions>

      {isLoading && <Loading />}
    </Dialog>
  );
};

export default EventTypeFormModal;
